import { useEffect, useMemo } from "react";
import { Download } from "lucide-react";
import { Button } from "@/shared/components/Button";
import {
  TEXTO_JA_INSTALOU,
  detectarAmbiente,
  instrucoesDeInstalacao,
} from "@/shared/lib/instalacaoPwa";

interface ModalInstalarAppProps {
  aberto: boolean;
  onFechar: () => void;
}

/**
 * Passo a passo da instalação para quando o navegador não deixa o Elos abrir a janela de
 * instalação com um toque (iPhone, Firefox e outros). O texto muda conforme o navegador.
 */
export function ModalInstalarApp({ aberto, onFechar }: ModalInstalarAppProps) {
  const instrucoes = useMemo(
    () =>
      instrucoesDeInstalacao(
        detectarAmbiente(navigator.userAgent, navigator.platform, navigator.maxTouchPoints),
      ),
    [],
  );

  useEffect(() => {
    if (!aberto) return;
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") onFechar();
    }
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-titulo"
      onClick={onFechar}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Download size={24} className="text-primary" />
          </div>
          <h2 id="modal-titulo" className="text-base font-semibold text-text">
            Como instalar o Elos
          </h2>
          <p className="text-sm leading-relaxed text-muted">{instrucoes.intro}</p>
        </div>

        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold leading-relaxed text-text">
          {instrucoes.passos.map((passo) => (
            <li key={passo}>{passo}</li>
          ))}
        </ol>

        {instrucoes.dica && (
          <p className="mt-3 text-sm leading-relaxed text-muted">{instrucoes.dica}</p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-muted">{TEXTO_JA_INSTALOU}</p>

        <div className="mt-6 flex">
          <Button
            onClick={onFechar}
            className="flex-1 bg-primary text-on-primary hover:bg-primary-hover"
          >
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );
}
